<script setup>
// After a password sign-in, like a bank app (docs/pwa/SPEC.md, decisions 20–21):
// a new PIN is required, then Face ID is offered once if this device can use it.
import axios from 'axios';
import { computed, nextTick, ref } from 'vue';
import { extractApiError } from '../apiError';
import { locale, t } from '../i18n';
import {
  biometricWords, declineFaceId, isCancelled, isFaceIdDeclined, isWebAuthnSupported, registerPasskey,
  storedPasskeyId,
} from '../webauthn';
import PinPad from './PinPad.vue';

// done(lockState): onboarding finished; signed-out: the user left instead.
const emit = defineEmits(['done', 'signed-out']);

const step = ref('choose');
const firstPin = ref('');
const busy = ref(false);
const error = ref('');
const errorKey = ref(0);
const pad = ref(null);
let lockState = null;

const method = computed(() => biometricWords(locale.value));

const title = computed(() => {
  if (step.value === 'faceid') {
    return t('onboarding.faceIdTitle', method.value);
  }
  return step.value === 'choose' ? t('security.choosePin') : t('security.repeatPin');
});

function shouldOfferFaceId() {
  return isWebAuthnSupported() && storedPasskeyId() === null && !isFaceIdDeclined();
}

async function onPin(pin) {
  error.value = '';
  if (step.value === 'choose') {
    firstPin.value = pin;
    step.value = 'repeat';
    await nextTick();
    pad.value?.clear();
    return;
  }

  if (pin !== firstPin.value) {
    firstPin.value = '';
    error.value = t('security.pinMismatch');
    errorKey.value += 1;
    step.value = 'choose';
    return;
  }

  busy.value = true;
  try {
    const { data } = await axios.put('/app-lock/pin', { pin });
    lockState = data.lock;
    if (shouldOfferFaceId()) {
      step.value = 'faceid';
    } else {
      emit('done', lockState);
    }
  } catch (e) {
    if (e?.response?.status === 401) {
      emit('signed-out', 'session');
      return;
    }
    error.value = extractApiError(e, t('security.failed'));
    errorKey.value += 1;
    step.value = 'choose';
  } finally {
    busy.value = false;
  }
}

async function enableFaceId() {
  busy.value = true;
  error.value = '';
  try {
    const data = await registerPasskey();
    emit('done', { ...lockState, has_passkey: data.passkeys.length > 0 });
  } catch (e) {
    if (!isCancelled(e)) {
      error.value = extractApiError(e, t('security.passkeyAddFailed'));
    }
  } finally {
    busy.value = false;
  }
}

function skipFaceId() {
  declineFaceId();
  emit('done', lockState);
}

async function signOut() {
  busy.value = true;
  try {
    await axios.post('/auth/logout');
  } catch {
    // Already signed out or offline: the login form is shown either way.
  } finally {
    busy.value = false;
    emit('signed-out', 'manual');
  }
}
</script>

<template>
  <div
    class="fixed inset-0 z-[200] flex h-dvh flex-col items-center overflow-y-auto bg-slate-950 px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-[max(env(safe-area-inset-top),2rem)] text-slate-100"
    role="dialog"
    aria-modal="true"
    aria-labelledby="onboarding-title"
  >
    <div class="flex w-full max-w-xs flex-1 flex-col items-center justify-center">
      <span
        class="flex size-12 items-center justify-center rounded-xl bg-slate-900 text-base font-bold tracking-tight text-emerald-400 ring-1 ring-slate-700/80"
        aria-hidden="true"
      >DT</span>

      <h2
        id="onboarding-title"
        class="mt-8 text-center text-lg font-semibold"
      >
        {{ title }}
      </h2>

      <template v-if="step !== 'faceid'">
        <p class="mt-2 text-center text-sm text-slate-400">
          {{ t('onboarding.pinHint') }}
        </p>
        <PinPad
          ref="pad"
          class="mt-6"
          :disabled="busy"
          :error-key="errorKey"
          @complete="onPin"
        />
      </template>

      <template v-else>
        <svg
          class="mt-8 size-16 text-slate-300"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 8V5a2 2 0 0 1 2-2h3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3" />
          <path d="M9 9v1.5M15 9v1.5M12 9v4h-1M9.5 16a3.5 3.5 0 0 0 5 0" />
        </svg>
        <p class="mt-6 text-center text-sm text-slate-400">
          {{ t('onboarding.faceIdHint', method) }}
        </p>
        <button
          type="button"
          class="mt-8 h-12 w-full rounded-xl bg-emerald-600 font-medium transition hover:bg-emerald-500 disabled:opacity-60"
          :disabled="busy"
          @click="enableFaceId"
        >
          {{ t('onboarding.faceIdUse', method) }}
        </button>
        <button
          type="button"
          class="mt-3 h-12 w-full rounded-xl text-sm text-slate-300 transition hover:text-slate-100 disabled:opacity-60"
          :disabled="busy"
          @click="skipFaceId"
        >
          {{ t('onboarding.faceIdSkip') }}
        </button>
      </template>

      <p
        class="mt-4 min-h-10 text-center text-sm text-rose-400"
        role="alert"
      >
        {{ error }}
      </p>
    </div>

    <button
      v-if="step !== 'faceid'"
      type="button"
      class="mt-6 py-2 text-sm text-slate-400 transition hover:text-slate-200"
      :disabled="busy"
      @click="signOut"
    >
      {{ t('app.logout') }}
    </button>
  </div>
</template>
